import { useState, useEffect, useRef, useCallback } from 'react';
// Marker used by automated headless tests to avoid requestFullscreen crashes
if (typeof window !== 'undefined') {
  window.__PLAYWRIGHT__ = window.__PLAYWRIGHT__ || false;
}

/*
  FullscreenCamera: true fullscreen (user requested).
  NOTE: requestFullscreen() can be flaky in some Chrome camera contexts, but we only invoke it when fullscreen is opened.
*/

import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ZoomIn, ZoomOut, SlidersHorizontal,
  Camera, SunDim, MoonStar, Stars, Wand2, ScanEye, 
  Radar, Scan, Cctv, Flame, Activity, Crosshair, Crown,
  RotateCcw, Image, Maximize2, Plus, Minus,
  Eye, Sparkles, Settings, Sliders, Hand
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { combineFilters, getFilterOverlay } from '../utils/liveFilters';
import GestureControls from './GestureControls';

// Fullscreen Filter Wheel Icon - Chrome Metallic
const FullscreenFilterIcon = ({ icon: Icon, label, active, locked, onClick, color }) => {
  const [isPressed, setIsPressed] = useState(false);
  
  return (
    <motion.button
      onTapStart={() => setIsPressed(true)}
      onTap={() => setIsPressed(false)}
      onTapCancel={() => setIsPressed(false)}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      disabled={locked}
      className="flex flex-col items-center gap-1"
      data-testid={`fullscreen-filter-${label.toLowerCase()}`}
    >
      <div 
        className={`
          relative w-10 h-10 rounded-xl flex items-center justify-center 
          transition-all duration-200 overflow-hidden
          ${active ? 'scale-110' : ''}
          ${locked ? 'opacity-40' : ''}
        `}
        style={{
          background: active 
            ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`
            : `linear-gradient(145deg, 
                rgba(110,120,145,1) 0%, 
                rgba(70,80,100,1) 35%, 
                rgba(50,60,80,1) 65%, 
                rgba(110,120,145,1) 100%)`,
          boxShadow: active 
            ? `0 8px 25px ${color}60, inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.3)`
            : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.2), inset 0 -2px 4px rgba(0,0,0,0.3)',
          border: active ? `2px solid ${color}` : '2px solid rgba(255,255,255,0.15)',
          transform: isPressed ? 'scale(0.92)' : active ? 'scale(1.1)' : 'scale(1)'
        }}
      >
        {/* Chrome glossy overlay */}
        <div 
          className="absolute inset-0 rounded-xl pointer-events-none"
          style={{
            background: 'linear-gradient(160deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.15) 30%, transparent 50%)'
          }}
        />
        
        <Icon 
          className="w-4 h-4 relative z-10" 
          style={{ 
            color: active ? '#fff' : 'rgba(255,255,255,0.9)',
            filter: active ? `drop-shadow(0 0 8px ${color})` : 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))'
          }}
        />
        
        {locked && (
          <div 
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, #ffd700, #ff8800)',
              boxShadow: '0 2px 8px rgba(255,215,0,0.5)'
            }}
          >
            <Crown className="w-3 h-3 text-black" />
          </div>
        )}
      </div>
      
      <span 
        className="text-[9px] font-bold"
        style={{ 
          color: active ? '#fff' : 'rgba(255,255,255,0.6)',
          textShadow: active ? `0 0 10px ${color}` : 'none'
        }}
      >
        {label}
      </span>
    </motion.button>
  );
};

const FullscreenCamera = ({ 
  isOpen, 
  onClose, 
  videoRef: externalVideoRef,
  stream,
  filters,
  toggleFilter,
  isPremium,
  isOwner,
  onCapture,
  onStartRecording,
  onStopRecording,
  isRecording,
  recordingDuration,
  zoomLevel,
  setZoomLevel,
  applyZoom,
  onSwitchCamera,
  facingMode,
  MAX_ZOOM = 15,
  setFilters,
  capturedImage,
  analysisResult,
  isAnalyzing,
  onAnalyze
}) => {
  const { theme } = useTheme();

  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false); // Slide-out settings panel
  
  // Screen Adjustments State
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100
  });
  
  // Preset Mode States
  const [moonMode, setMoonMode] = useState(false);
  const [meteorMode, setMeteorMode] = useState(false);
  const [issMode, setIssMode] = useState(false);
  const savedZoom = useRef(1);

  const { t } = useLanguage();
  const internalVideoRef = useRef(null);
  const containerRef = useRef(null);
  const gestureContainerRef = useRef(null); // For gesture controls
  const canvasRef = useRef(null);
  const isUnlocked = isPremium || isOwner;
  const [mode, setMode] = useState('photo');
  const [hiddenCaptureId, setHiddenCaptureId] = useState(null);

  // Derived: show preview when we have a new image that user hasn't dismissed
  const showCapturePreview = capturedImage && hiddenCaptureId !== capturedImage;

  // Reset preset modes when fullscreen closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all preset modes when exiting fullscreen
      if (moonMode || meteorMode || issMode) {
        // Restore original zoom if any mode was active
        if (applyZoom && savedZoom.current) {
          applyZoom(savedZoom.current);
        } else if (setZoomLevel && savedZoom.current) {
          setZoomLevel(savedZoom.current);
        }
        // Turn off filters
        setFilters?.(prev => ({ 
          ...prev, 
          night_mode: false, 
          star_enhancement: false 
        }));
      }
      setMoonMode(false);
      setMeteorMode(false);
      setIssMode(false);
      savedZoom.current = 1;
    }
  }, [isOpen]);

  // If user switches away from video mode, stop recording
  useEffect(() => {
    if (mode !== 'video' && isRecording) {
      onStopRecording?.();
    }
  }, [mode, isRecording, onStopRecording]);

  // Filter definitions - Military-Grade Filter System
  const allFilters = [
    { id: 'solar_eclipse', icon: Eye, label: 'Eclipse', color: '#ff4500', premium: false },
    { id: 'sun_dimming', icon: SunDim, label: t('solar'), color: '#ff6b00', premium: false },
    { id: 'night_mode', icon: MoonStar, label: t('night'), color: '#8b5cf6', premium: false },
    { id: 'noise_reduction', icon: Wand2, label: t('clarity'), color: '#00f2ea', premium: false },
    { id: 'star_enhancement', icon: Stars, label: t('stars'), color: '#ffd700', premium: true },
    { id: 'deep_sky', icon: Sparkles, label: 'Deep Sky', color: '#9966ff', premium: true },
    { id: 'anomaly_highlight', icon: Radar, label: t('anomaly'), color: '#ff2e2e', premium: true },
    { id: '4k_upscale', icon: Scan, label: '4K', color: '#00f2ea', premium: true },
    { id: '8k_ultra', icon: Scan, label: '8K', color: '#00d4ff', premium: true },
    { id: 'night_vision', icon: ScanEye, label: 'NV Gen3', color: '#39ff14', premium: true },
    { id: 'night_vision_white', icon: ScanEye, label: 'NV White', color: '#e0e0e0', premium: true },
    { id: 'starlight', icon: Stars, label: 'Starlight', color: '#87ceeb', premium: true },
    { id: 'infrared', icon: Cctv, label: t('infrared'), color: '#ff6600', premium: true },
    { id: 'thermal', icon: Flame, label: t('thermal'), color: '#ff3366', premium: true },
    { id: 'thermal_ironbow', icon: Flame, label: 'Ironbow', color: '#ff9900', premium: true },
    { id: 'motion_enhance', icon: Activity, label: 'Motion', color: '#a855f7', premium: true },
    { id: 'object_tracking', icon: Crosshair, label: t('track'), color: '#22c55e', premium: true },
    { id: 'ufo_track', icon: Radar, label: 'UAP', color: '#00ff88', premium: true },
  ];

  // Active filters for live preview
  const activeFilters = Object.entries(filters || {})
    .filter(([_, active]) => active)
    .map(([name]) => name);
  
  const liveFilterStyle = combineFilters(activeFilters);

  const isVideoMode = mode === 'video';

  // Sync video stream from external source
  useEffect(() => {
    if (isOpen && stream && internalVideoRef.current) {
      internalVideoRef.current.srcObject = stream;
      internalVideoRef.current.play().catch(e => console.log('Video play error:', e));
    }
  }, [isOpen, stream]);

  // Ensure video keeps playing when returning to the app / tab (Chrome can pause it)
  useEffect(() => {
    if (!isOpen) return;

    const resume = () => {
      if (internalVideoRef.current) {
        internalVideoRef.current.play().catch(() => {});
      }
    };

    const onFsChange = () => {
      // If user exits browser fullscreen, close overlay too
      if (isOpen && !document.fullscreenElement && !document.webkitFullscreenElement) {
        onClose();
      }
    };

    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [isOpen, onClose]);

  // Also sync when externalVideoRef changes
  useEffect(() => {
    if (isOpen && externalVideoRef?.current?.srcObject && internalVideoRef.current) {
      internalVideoRef.current.srcObject = externalVideoRef.current.srcObject;
      internalVideoRef.current.play().catch(e => console.log('Video play error:', e));
    }
  }, [isOpen, externalVideoRef]);

  // Handle true fullscreen
  // NOTE: Avoid calling requestFullscreen() in headless environments (can crash browser automation).
  useEffect(() => {
    if (!isOpen) return;

    const el = containerRef.current;
    if (!el) return;

    const enter = async () => {
      try {
        const isHeadless = !!window.__PLAYWRIGHT__;
        if (isHeadless) return;

        if (document.fullscreenElement) return;
        if (el.requestFullscreen) {
          await el.requestFullscreen({ navigationUI: 'hide' });
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        }
      } catch (e) {
        console.log('requestFullscreen failed:', e);
      }
    };

    // Only attempt fullscreen after a short delay to ensure a user gesture opened the overlay
    setTimeout(() => enter(), 0);
  }, [isOpen]);

  // Exit handler
  const handleExit = useCallback(async () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      }
    } catch (e) {
      console.log('exitFullscreen failed:', e);
    }
    onClose();
  }, [onClose]);

  // Listen for escape and fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement && isOpen) {
        // User exited fullscreen via browser controls
        // Don't auto-close, let them stay in the view
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleExit();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleExit]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCapturePressStart = () => {
    // No longer used for video - video uses tap toggle
  };

  const handleCapturePressEnd = () => {
    if (isVideoMode) {
      // Toggle recording on tap
      if (isRecording) {
        onStopRecording?.();
      } else {
        onStartRecording?.();
      }
      return;
    }

    // Photo capture is a click/tap
    onCapture?.();
  };


  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black"
        data-testid="fullscreen-camera-view"
        style={{ touchAction: 'none' }}
      >
        {/* Exit Button - Top Right */}
        <motion.button
          data-testid="fullscreen-exit-btn"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          onClick={handleExit}
          className="absolute top-3 right-3 z-50 p-3 rounded-full chrome-btn-dark"
          style={{
            background: 'linear-gradient(145deg, rgba(100,110,130,1), rgba(60,70,90,1))',
            boxShadow: '0 4px 15px rgba(0,0,0,0.5), inset 0 2px 3px rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.15)'
          }}
        >
          <X className="w-6 h-6 text-white" />
        </motion.button>

        {/* Top Info Bar */}
        <div className="absolute top-3 left-3 z-50 flex items-center gap-3">
          {/* Zoom Level */}
          <div 
            className="px-4 py-2 rounded-full font-mono font-bold text-lg"
            style={{
              background: 'linear-gradient(145deg, rgba(100,110,130,1), rgba(60,70,90,1))',
              boxShadow: '0 4px 15px rgba(0,0,0,0.5), inset 0 2px 3px rgba(255,255,255,0.2)',
              color: zoomLevel >= 10 ? '#ffd700' : '#fff',
              border: '1px solid rgba(255,255,255,0.15)'
            }}
          >
            {zoomLevel.toFixed(1)}x
          </div>
          
          {/* Camera indicator */}
          <div 
            className="px-3 py-2 rounded-full text-xs font-bold"
            style={{
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#00f2ea'
            }}
          >
            {facingMode === 'environment' ? 'REAR' : 'FRONT'}
          </div>
        </div>

        {/* Recording Indicator */}
        {isRecording && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: 'rgba(255,0,0,0.3)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,0,0,0.5)'
            }}
          >
            <motion.div 
              className="w-3 h-3 rounded-full bg-red-500"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="text-white font-mono font-bold">{formatTime(recordingDuration || 0)}</span>
          </motion.div>
        )}

        {/* Camera Feed - Full Screen */}
        <div className="fixed inset-0 overflow-hidden bg-black">
          <video
            ref={internalVideoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              // Native camera zoom only - no CSS scaling
              imageRendering: 'high-quality',
              filter: `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)${activeFilters.length > 0 ? ` ${liveFilterStyle?.filter || ''}` : ''}`,
            }}
          />

          {/* Filter Overlays */}
          {activeFilters.length > 0 && getFilterOverlay(activeFilters, theme?.colors || {}).map((overlay, index) => (
            <div 
              key={`overlay-${overlay.type}-${index}`}
              className="absolute inset-0 pointer-events-none"
              style={overlay.style}
            />
          ))}

          {/* Gesture Detection Area - Full Screen Touch */}
          <div 
            ref={gestureContainerRef}
            className="absolute inset-0 z-30"
            style={{ touchAction: 'none' }}
          />
          
          {/* Gesture Controls */}
          <GestureControls
            containerRef={gestureContainerRef}
            enabled={!showFilters && !showSettings}
            onZoom={(newZoom) => {
              if (applyZoom) {
                applyZoom(newZoom);
              } else {
                setZoomLevel?.(newZoom);
              }
            }}
            zoomLevel={zoomLevel || 1}
            maxZoom={MAX_ZOOM}
            minZoom={0.5}
            onCapture={onCapture}
            onStartRecording={onStartRecording}
            onStopRecording={onStopRecording}
            isRecording={isRecording}
            audioEnabled={true}
          />

          {/* Capture Preview Overlay */}
          <AnimatePresence>
            {showCapturePreview && capturedImage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-50 bg-black"
              >
                {/* Captured Image */}
                <img 
                  src={capturedImage} 
                  alt="Captured" 
                  className="absolute inset-0 w-full h-full object-contain"
                />
                
                {/* Analysis Result Overlay */}
                {(isAnalyzing || analysisResult) && (
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-60">
                    <motion.div
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      className="rounded-2xl p-4 max-h-[40vh] overflow-y-auto"
                      style={{
                        background: 'rgba(0,0,0,0.85)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${theme?.colors?.primary || '#00f2ea'}40`
                      }}
                    >
                      {isAnalyzing ? (
                        <div className="flex items-center justify-center gap-3 py-4">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="w-6 h-6 border-2 border-t-transparent rounded-full"
                            style={{ borderColor: `${theme?.colors?.primary || '#00f2ea'}`, borderTopColor: 'transparent' }}
                          />
                          <span className="text-white font-exo2">Analyzing with AI...</span>
                        </div>
                      ) : analysisResult && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-lg">🔍</span>
                            <h3 className="font-unbounded font-bold text-white">Analysis Result</h3>
                          </div>
                          
                          {/* Anomaly Status */}
                          <div 
                            className="px-3 py-2 rounded-lg mb-3 flex items-center gap-2"
                            style={{
                              background: analysisResult.anomalies_detected 
                                ? 'rgba(255,46,46,0.2)' 
                                : 'rgba(34,197,94,0.2)',
                              border: `1px solid ${analysisResult.anomalies_detected ? 'rgba(255,46,46,0.4)' : 'rgba(34,197,94,0.4)'}`
                            }}
                          >
                            <span className="text-lg">{analysisResult.anomalies_detected ? '⚠️' : '✅'}</span>
                            <span className="font-bold text-sm" style={{ color: analysisResult.anomalies_detected ? '#ff6b6b' : '#4ade80' }}>
                              {analysisResult.anomalies_detected ? 'Anomalies Detected!' : 'No Anomalies'}
                            </span>
                          </div>
                          
                          {/* Analysis Text */}
                          <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                            {analysisResult.analysis?.substring(0, 500)}
                            {analysisResult.analysis?.length > 500 && '...'}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="absolute top-4 right-4 z-60 flex gap-2">
                  {/* Analyze Button */}
                  {!isAnalyzing && !analysisResult && (
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onAnalyze?.()}
                      className="px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2"
                      style={{
                        background: `linear-gradient(145deg, ${theme?.colors?.primary || '#00f2ea'}, ${theme?.colors?.accent || '#a855f7'})`,
                        color: 'white',
                        boxShadow: `0 4px 15px ${theme?.colors?.primary || '#00f2ea'}50`
                      }}
                    >
                      <span>✨</span> Analyze
                    </motion.button>
                  )}
                  
                  {/* Back to Camera Button */}
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setHiddenCaptureId(capturedImage);
                    }}
                    className="px-4 py-2 rounded-full font-bold text-sm"
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.3)'
                    }}
                  >
                    ← Back
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* No camera message */}
          {!stream && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white/50">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="font-exo2">Camera not available</p>
                <p className="text-sm mt-2">Grant camera permission to use fullscreen mode</p>
              </div>
            </div>
          )}

          {/* Right Side Controls - All in One Vertical Strip */}
          <div className="absolute right-3 top-1/3 -translate-y-1/2 z-50 flex flex-col items-center gap-2">
            {/* Zoom Controls Container */}
            <div 
              className="flex flex-col items-center gap-1.5 px-2 py-2 rounded-2xl"
              style={{
                background: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.15)'
              }}
            >
              {/* Zoom In */}
              <button
                onClick={() => {
                  const newZoom = Math.min((zoomLevel || 1) * 1.5, MAX_ZOOM);
                  if (applyZoom) {
                    applyZoom(newZoom);
                  } else {
                    setZoomLevel?.(newZoom);
                  }
                }}
                className="w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{ background: 'rgba(0,242,234,0.15)', border: '1px solid rgba(0,242,234,0.3)' }}
                data-testid="fullscreen-zoom-in"
              >
                <Plus className="w-5 h-5 text-cyan-400" />
              </button>
              
              {/* Zoom Level */}
              <span 
                className="font-mono text-sm font-bold py-1"
                style={{ color: (zoomLevel || 1) >= 10 ? '#00f2ea' : '#fff' }}
              >
                {(zoomLevel || 1).toFixed(0)}x
              </span>
              
              {/* Zoom Out */}
              <button
                onClick={() => {
                  const newZoom = Math.max((zoomLevel || 1) / 1.5, 1);
                  if (applyZoom) {
                    applyZoom(newZoom);
                  } else {
                    setZoomLevel?.(newZoom);
                  }
                }}
                className="w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                style={{ background: 'rgba(0,242,234,0.15)', border: '1px solid rgba(0,242,234,0.3)' }}
                data-testid="fullscreen-zoom-out"
              >
                <Minus className="w-5 h-5 text-cyan-400" />
              </button>
            </div>

            {/* Divider */}
            <div className="w-8 h-px bg-white/20 my-1" />

            {/* Preset Modes */}
            {/* Moon Mode */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                const newMode = !moonMode;
                if (newMode) {
                  savedZoom.current = zoomLevel || 1;
                  const targetZoom = 10;
                  if (applyZoom) {
                    applyZoom(targetZoom);
                  } else {
                    setZoomLevel?.(targetZoom);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: true }));
                } else {
                  if (applyZoom) {
                    applyZoom(savedZoom.current);
                  } else {
                    setZoomLevel?.(savedZoom.current);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: false }));
                }
                setMoonMode(newMode);
                setMeteorMode(false);
                setIssMode(false);
              }}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${moonMode ? 'ring-2 ring-purple-400 ring-offset-2 ring-offset-black' : ''}`}
              style={{
                background: moonMode 
                  ? 'linear-gradient(145deg, rgba(139,92,246,0.8), rgba(109,62,216,0.8))' 
                  : 'linear-gradient(145deg, rgba(60,70,90,0.9), rgba(40,50,70,0.9))',
                boxShadow: moonMode 
                  ? '0 4px 20px rgba(139,92,246,0.5), inset 0 2px 4px rgba(255,255,255,0.2)' 
                  : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.1)',
                border: moonMode ? '2px solid #8b5cf6' : '1px solid rgba(255,255,255,0.15)'
              }}
              data-testid="fullscreen-moon-mode"
            >
              <span className="text-xl">🌙</span>
            </motion.button>

            {/* Meteor Mode */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                const newMode = !meteorMode;
                if (newMode) {
                  savedZoom.current = zoomLevel || 1;
                  const targetZoom = 1;
                  if (applyZoom) {
                    applyZoom(targetZoom);
                  } else {
                    setZoomLevel?.(targetZoom);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: true, star_enhancement: isUnlocked }));
                } else {
                  if (applyZoom) {
                    applyZoom(savedZoom.current);
                  } else {
                    setZoomLevel?.(savedZoom.current);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: false, star_enhancement: false }));
                }
                setMeteorMode(newMode);
                setMoonMode(false);
                setIssMode(false);
              }}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${meteorMode ? 'ring-2 ring-orange-400 ring-offset-2 ring-offset-black' : ''}`}
              style={{
                background: meteorMode 
                  ? 'linear-gradient(145deg, rgba(255,165,0,0.8), rgba(225,135,0,0.8))' 
                  : 'linear-gradient(145deg, rgba(60,70,90,0.9), rgba(40,50,70,0.9))',
                boxShadow: meteorMode 
                  ? '0 4px 20px rgba(255,165,0,0.5), inset 0 2px 4px rgba(255,255,255,0.2)' 
                  : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.1)',
                border: meteorMode ? '2px solid #ffa500' : '1px solid rgba(255,255,255,0.15)'
              }}
              data-testid="fullscreen-meteor-mode"
            >
              <span className="text-xl">☄️</span>
            </motion.button>

            {/* ISS Mode */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                const newMode = !issMode;
                if (newMode) {
                  savedZoom.current = zoomLevel || 1;
                  const targetZoom = 5;
                  if (applyZoom) {
                    applyZoom(targetZoom);
                  } else {
                    setZoomLevel?.(targetZoom);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: true }));
                } else {
                  if (applyZoom) {
                    applyZoom(savedZoom.current);
                  } else {
                    setZoomLevel?.(savedZoom.current);
                  }
                  setFilters?.(prev => ({ ...prev, night_mode: false }));
                }
                setIssMode(newMode);
                setMoonMode(false);
                setMeteorMode(false);
              }}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${issMode ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-black' : ''}`}
              style={{
                background: issMode 
                  ? 'linear-gradient(145deg, rgba(0,191,255,0.8), rgba(0,161,225,0.8))' 
                  : 'linear-gradient(145deg, rgba(60,70,90,0.9), rgba(40,50,70,0.9))',
                boxShadow: issMode 
                  ? '0 4px 20px rgba(0,191,255,0.5), inset 0 2px 4px rgba(255,255,255,0.2)' 
                  : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.1)',
                border: issMode ? '2px solid #00bfff' : '1px solid rgba(255,255,255,0.15)'
              }}
              data-testid="fullscreen-iss-mode"
            >
              <span className="text-lg">🛰️</span>
            </motion.button>

            {/* Divider before Adjustments */}
            <div className="w-8 h-px bg-white/20 my-1" />

            {/* Adjustments Button - Directly under ISS */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowSettings(!showSettings)}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${showSettings ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-black' : ''}`}
              style={{
                background: showSettings 
                  ? 'linear-gradient(145deg, rgba(0,242,234,0.8), rgba(0,196,189,0.8))' 
                  : 'linear-gradient(145deg, rgba(60,70,90,0.9), rgba(40,50,70,0.9))',
                boxShadow: showSettings 
                  ? '0 4px 20px rgba(0,242,234,0.5), inset 0 2px 4px rgba(255,255,255,0.2)' 
                  : '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.1)',
                border: showSettings ? '2px solid #00f2ea' : '1px solid rgba(255,255,255,0.15)'
              }}
              data-testid="fullscreen-adjustments-btn"
            >
              <Sliders className="w-5 h-5 text-white" />
            </motion.button>
          </div>

          {/* Adjustments Panel - Positioned next to the button */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, x: 20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 20, scale: 0.95 }}
                transition={{ type: 'spring', damping: 25, stiffness: 400 }}
                className="absolute top-1/2 right-20 -translate-y-1/4 z-50 w-48 rounded-xl overflow-hidden"
                style={{
                  background: 'rgba(0,0,0,0.9)',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255,255,255,0.15)'
                }}
              >
                <div className="p-3">
                  <p className="text-white/80 text-xs font-bold mb-2">Adjustments</p>
                  
                  {/* Brightness */}
                  <div className="mb-2">
                    <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                      <span>Brightness</span>
                      <span>{adjustments.brightness}%</span>
                    </div>
                    <input 
                      type="range" 
                      min="50" 
                      max="150" 
                      value={adjustments.brightness}
                      onChange={(e) => setAdjustments(prev => ({ ...prev, brightness: parseInt(e.target.value) }))}
                      className="w-full h-1.5 rounded appearance-none cursor-pointer"
                      style={{ background: 'linear-gradient(to right, #333, #00f2ea)', accentColor: '#00f2ea' }}
                    />
                  </div>

                  {/* Contrast */}
                  <div className="mb-2">
                    <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                      <span>Contrast</span>
                      <span>{adjustments.contrast}%</span>
                    </div>
                    <input 
                      type="range" 
                      min="50" 
                      max="150" 
                      value={adjustments.contrast}
                      onChange={(e) => setAdjustments(prev => ({ ...prev, contrast: parseInt(e.target.value) }))}
                      className="w-full h-1.5 rounded appearance-none cursor-pointer"
                      style={{ background: 'linear-gradient(to right, #333, #00f2ea)', accentColor: '#00f2ea' }}
                    />
                  </div>

                  {/* Saturation */}
                  <div className="mb-2">
                    <div className="flex justify-between text-[10px] text-white/60 mb-0.5">
                      <span>Saturation</span>
                      <span>{adjustments.saturation}%</span>
                    </div>
                    <input 
                      type="range" 
                      min="0" 
                      max="200" 
                      value={adjustments.saturation}
                      onChange={(e) => setAdjustments(prev => ({ ...prev, saturation: parseInt(e.target.value) }))}
                      className="w-full h-1.5 rounded appearance-none cursor-pointer"
                      style={{ background: 'linear-gradient(to right, #333, #00f2ea)', accentColor: '#00f2ea' }}
                    />
                  </div>

                  {/* Reset */}
                  <button
                    onClick={() => setAdjustments({ brightness: 100, contrast: 100, saturation: 100 })}
                    className="w-full py-1.5 mt-1 rounded-lg text-[10px] font-bold text-cyan-400"
                    style={{ background: 'rgba(0,242,234,0.1)', border: '1px solid rgba(0,242,234,0.3)' }}
                  >
                    Reset
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>


        </div>

        {/* Bottom Control Area with Filter Wheel */}
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.1, type: 'spring', damping: 20 }}
          className="absolute left-0 right-0 bottom-0 z-40"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.98) 0%, rgba(0,0,0,0.9) 50%, transparent 100%)',
            paddingTop: '30px'
          }}
        >
          {/* Mode Toggle */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              onClick={() => setMode('video')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                isVideoMode ? 'text-black' : 'text-white/50'
              }`}
              style={{
                background: isVideoMode 
                  ? 'linear-gradient(145deg, #fff, #e0e0e0)' 
                  : 'transparent',
                boxShadow: isVideoMode 
                  ? '0 4px 15px rgba(255,255,255,0.3), inset 0 2px 3px rgba(255,255,255,0.5)' 
                  : 'none'
              }}
            >
              {t('videoMode')}
            </button>
            <button
              onClick={() => setMode('photo')}
              className={`px-6 py-2 rounded-full text-sm font-bold transition-all ${
                !isVideoMode ? 'text-black' : 'text-white/50'
              }`}
              style={{
                background: !isVideoMode 
                  ? 'linear-gradient(145deg, #fff, #e0e0e0)' 
                  : 'transparent',
                boxShadow: !isVideoMode 
                  ? '0 4px 15px rgba(255,255,255,0.3), inset 0 2px 3px rgba(255,255,255,0.5)' 
                  : 'none'
              }}
            >
              {t('photo')}
            </button>
          </div>

          {/* Filters (collapsed) */}
          <div className="px-3 py-2 flex items-center justify-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFilters(true)}
              className="px-4 py-2 rounded-full font-mono text-xs tracking-wider flex items-center gap-2"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.9)'
              }}
              data-testid="fullscreen-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </motion.button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-[120]"
              >
                <div
                  className="absolute inset-0"
                  style={{ background: 'rgba(0,0,0,0.75)' }}
                  onClick={() => setShowFilters(false)}
                />

                <motion.div
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 40, opacity: 0 }}
                  className="absolute left-3 right-3 bottom-28 rounded-2xl overflow-hidden"
                  style={{
                    background: 'rgba(10,12,18,0.96)',
                    border: '1px solid rgba(255,255,255,0.12)'
                  }}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div className="font-exo2 text-sm text-white/90">Filters</div>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="p-2 rounded-xl hover:bg-white/5"
                      data-testid="fullscreen-filters-close"
                    >
                      <X className="w-4 h-4 text-white/70" />
                    </button>
                  </div>

                  <div className="max-h-[45vh] overflow-y-auto p-3">
                    <div className="grid grid-cols-4 gap-2">
                      {allFilters.map((filter) => (
                        <FullscreenFilterIcon
                          key={filter.id}
                          icon={filter.icon}
                          label={filter.label}
                          active={filters?.[filter.id]}
                          locked={filter.premium && !isUnlocked}
                          onClick={() => {
                            // Block premium filters if not unlocked
                            if (filter.premium && !isUnlocked) {
                              return;
                            }
                            toggleFilter?.(filter.id);
                          }}
                          color={filter.color}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Capture Button Row */}
          <div className="flex items-center justify-center gap-8 py-5 pb-8">
            {/* Gallery Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              className="w-14 h-14 rounded-2xl flex items-center justify-center chrome-btn-dark"
              onClick={() => window.location.assign('/gallery')}
              style={{
                background: 'linear-gradient(145deg, rgba(90,100,120,1), rgba(50,60,80,1))',
                boxShadow: '0 4px 15px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,255,255,0.15)',
                border: '2px solid rgba(255,255,255,0.1)'
              }}
              data-testid="fullscreen-gallery-btn"
            >
              <Image className="w-6 h-6 text-white/70" />
            </motion.button>

            {/* Main Capture Button - Chrome Metallic */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (isVideoMode) {
                  // Simple tap to toggle recording
                  if (isRecording) {
                    onStopRecording?.();
                  } else {
                    onStartRecording?.();
                  }
                } else {
                  // Photo capture
                  onCapture?.();
                }
              }}
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              data-testid="fullscreen-capture-btn"
            >
              {/* Outer ring - Chrome */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: isRecording
                    ? 'linear-gradient(145deg, #ff6666, #cc0000)'
                    : 'linear-gradient(145deg, #fff, #c0c0c0, #808080, #c0c0c0, #fff)',
                  boxShadow: isRecording 
                    ? '0 0 0 4px #ff2e2e, 0 8px 30px rgba(255,46,46,0.5)'
                    : '0 0 0 4px white, 0 10px 35px rgba(0,0,0,0.4), inset 0 3px 6px rgba(255,255,255,0.8)',
                }}
              >
                {/* Chrome shine */}
                <div 
                  className="absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 100%)' }}
                />
              </div>
              
              {/* Inner button */}
              <motion.div 
                className="relative flex items-center justify-center"
                animate={{
                  width: isRecording ? 28 : 60,
                  height: isRecording ? 28 : 60,
                  borderRadius: isRecording ? 6 : 30
                }}
                style={{
                  background: isVideoMode || isRecording 
                    ? 'linear-gradient(145deg, #ff4444, #cc0000)' 
                    : 'linear-gradient(145deg, #fff, #e8e8e8, #d0d0d0)',
                  boxShadow: isRecording
                    ? '0 6px 20px rgba(255,46,46,0.6), inset 0 2px 4px rgba(255,255,255,0.3)'
                    : '0 6px 20px rgba(255,255,255,0.3), inset 0 3px 6px rgba(255,255,255,0.8)'
                }}
              >
                {/* Shine */}
                <div 
                  className="absolute inset-x-0 top-0 h-1/2 pointer-events-none"
                  style={{
                    borderRadius: 'inherit',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, transparent 100%)'
                  }}
                />
              </motion.div>

              {/* Recording pulse */}
              {isRecording && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{ border: '3px solid #ff2e2e' }}
                  animate={{ scale: [1, 1.15, 1], opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              )}
            </motion.button>

            {/* Flip Camera Button - REMOVED */}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FullscreenCamera;
